/**
 * Canonical sales-order status machine + pure line-resolution helpers.
 *
 * These mirror the state transitions enforced in the backend RPCs
 * (allocate_sales_order, fulfil_sales_order, create_pick_task,
 * finalize_sales_return) so the UI and the database cannot drift apart.
 *
 * A sales order moves:
 *   draft → confirmed → allocated | partially_allocated → ready_to_pick
 *         → partially_fulfilled → fulfilled → completed
 *   (any pre-fulfilment state → cancelled)
 *   (fulfilled/partially_fulfilled → returned | partially_returned)
 */

import { humanize } from "./format";
import { providerDisplayName } from "./order-provenance";
import { ebayInboundCancelIsActionableBuyer } from "./inbound-buyer-cancel";

export const SO_STATUS = {
  DRAFT: "draft",
  CONFIRMED: "confirmed",
  ALLOCATED: "allocated",
  PARTIALLY_ALLOCATED: "partially_allocated",
  READY_TO_PICK: "ready_to_pick",
  PARTIALLY_FULFILLED: "partially_fulfilled",
  FULFILLED: "fulfilled",
  COMPLETED: "completed",
  RETURNED: "returned",
  PARTIALLY_RETURNED: "partially_returned",
  CANCELLED: "cancelled",
} as const;

export type SoStatus = (typeof SO_STATUS)[keyof typeof SO_STATUS];

/** Legacy status written before the "partial" split. Kept for read-compatibility. */
export const LEGACY_PARTIAL = "partial";

/** Every status a sales order may carry (incl. the legacy alias). */
export const ALL_SO_STATUSES: string[] = [
  ...Object.values(SO_STATUS),
  LEGACY_PARTIAL,
];

/** Statuses in which an order may still be allocated (pre-pick reservation). */
export const ALLOCATABLE_STATUSES: string[] = [
  SO_STATUS.CONFIRMED,
  SO_STATUS.ALLOCATED,
  SO_STATUS.PARTIALLY_ALLOCATED,
  LEGACY_PARTIAL,
];

/** Statuses in which an order may be fulfilled. */
export const FULFILLABLE_STATUSES: string[] = [
  SO_STATUS.CONFIRMED,
  SO_STATUS.ALLOCATED,
  SO_STATUS.PARTIALLY_ALLOCATED,
  SO_STATUS.READY_TO_PICK,
  SO_STATUS.PARTIALLY_FULFILLED,
  LEGACY_PARTIAL,
];

/** Statuses in which an order may be picked (a pick task can be started). */
export const PICKABLE_STATUSES: string[] = [
  SO_STATUS.CONFIRMED,
  SO_STATUS.ALLOCATED,
  SO_STATUS.PARTIALLY_ALLOCATED,
  SO_STATUS.PARTIALLY_FULFILLED,
  SO_STATUS.READY_TO_PICK,
  LEGACY_PARTIAL,
];

/** "Open" orders still awaiting some or all fulfilment. */
export const OPEN_STATUSES: string[] = [
  SO_STATUS.CONFIRMED,
  SO_STATUS.ALLOCATED,
  SO_STATUS.PARTIALLY_ALLOCATED,
  SO_STATUS.READY_TO_PICK,
  SO_STATUS.PARTIALLY_FULFILLED,
  LEGACY_PARTIAL,
];

/** Terminal statuses that can no longer be cancelled. */
export const TERMINAL_STATUSES: string[] = [
  SO_STATUS.FULFILLED,
  SO_STATUS.COMPLETED,
  SO_STATUS.CANCELLED,
  SO_STATUS.RETURNED,
  SO_STATUS.PARTIALLY_RETURNED,
];

/** Statuses that mean the order has had at least some stock shipped. */
export const FULFILLED_STATUSES: string[] = [
  SO_STATUS.FULFILLED,
  SO_STATUS.PARTIALLY_FULFILLED,
  SO_STATUS.COMPLETED,
  SO_STATUS.RETURNED,
  SO_STATUS.PARTIALLY_RETURNED,
];

export const isAllocatable = (s: string | null | undefined): boolean =>
  !!s && ALLOCATABLE_STATUSES.includes(s);

export const isFulfillable = (s: string | null | undefined): boolean =>
  !!s && FULFILLABLE_STATUSES.includes(s);

export const isPickable = (s: string | null | undefined): boolean =>
  !!s && PICKABLE_STATUSES.includes(s);

export const isOpen = (s: string | null | undefined): boolean =>
  !!s && OPEN_STATUSES.includes(s);

export const isCancellable = (s: string | null | undefined): boolean =>
  !!s && !TERMINAL_STATUSES.includes(s);

export const isFulfilled = (s: string | null | undefined): boolean =>
  !!s && FULFILLED_STATUSES.includes(s);

/** Physical line fields needed to decide whether allocation is still outstanding. */
export interface AllocatableLine {
  is_bundle?: boolean | null;
  qty_ordered: number;
  qty_allocated: number;
  qty_fulfilled: number;
  qty_cancelled?: number | null;
}

/**
 * Unallocated residual for a physical line. Same formula as `qty_backordered`
 * / `backorderedQuantity`; bundle parents contribute 0.
 */
export function lineUnallocatedQty(line: AllocatableLine): number {
  if (line.is_bundle) return 0;
  return backorderedQuantity(
    line.qty_ordered,
    line.qty_cancelled ?? 0,
    line.qty_fulfilled,
    line.qty_allocated,
  );
}

export function hasUnallocatedPhysicalQty(lines: AllocatableLine[]): boolean {
  return lines.some((line) => lineUnallocatedQty(line) > 0);
}

/**
 * True when Allocate should be the primary operator CTA: the order may still
 * be allocated and at least one physical line has unreserved quantity.
 * Direct fulfil remains available as a secondary shortcut.
 */
export function shouldPromoteAllocateCta(
  status: string | null | undefined,
  lines: AllocatableLine[],
): boolean {
  return isAllocatable(status) && hasUnallocatedPhysicalQty(lines);
}

/**
 * Operator label for residual quantity. Before any reservation or fulfilment,
 * the stored `qty_backordered` residual is unallocated — not a stock shortage.
 */
export function residualQtyLabel(allocated: number, fulfilled: number): "Unallocated" | "Backordered" {
  return allocated === 0 && fulfilled === 0 ? "Unallocated" : "Backordered";
}

/**
 * Line total = quantity × unit price − line discount + line tax.
 * This is the authoritative order-line arithmetic (mirrors the DB columns).
 */
export function lineTotal(
  quantity: number,
  unitPrice: number,
  discount = 0,
  tax = 0,
): number {
  return quantity * unitPrice - discount + tax;
}

/**
 * Order total = subtotal + tax + shipping − discount.
 * This is the authoritative order-header arithmetic — the exact formula the
 * backend `create_sales_order` persists (header/order-level tax is the single
 * authoritative tax source). Mirrors the DB so the create UI and the persisted
 * total can never drift apart.
 */
export function orderTotal(
  subtotal: number,
  tax: number,
  shipping: number,
  discount: number,
): number {
  return subtotal + tax + shipping - discount;
}

/**
 * Canonical SKU for an order line: the sellable variant's SKU wins, with the
 * product-level SKU as a fallback only when no variant is recorded.
 */
export function lineSku(
  variantSku: string | null | undefined,
  productSku: string | null | undefined,
): string {
  return variantSku || productSku || "";
}

/**
 * Human-readable variant label for a line. Prefers a stored snapshot
 * (variant_attributes JSONB), falling back to a joined variant label.
 */
export function lineVariantLabel(
  variantAttributes: Record<string, unknown> | null | undefined,
  joinedLabel: string | null | undefined,
): string {
  if (variantAttributes && typeof variantAttributes === "object") {
    const parts = Object.values(variantAttributes)
      .map((v) => String(v))
      .filter(Boolean);
    if (parts.length) return parts.join(" / ");
  }
  return joinedLabel ?? "";
}

/**
 * Residual backorder quantity for a line: the portion of the ordered amount
 * that is neither allocated, fulfilled, nor cancelled. Mirrors the backend
 * formula `GREATEST(ordered - cancelled - fulfilled - allocated, 0)`.
 */
export function remainingFulfilmentQty(
  ordered: number,
  cancelled: number,
  fulfilled: number,
): number {
  return Math.max(
    0,
    Math.floor(Number(ordered) || 0) - Math.max(0, Math.floor(Number(cancelled) || 0)) - Math.max(0, Math.floor(Number(fulfilled) || 0)),
  );
}

export function backorderedQuantity(
  ordered: number,
  cancelled: number,
  fulfilled: number,
  allocated: number,
): number {
  return Math.max(ordered - cancelled - fulfilled - allocated, 0);
}

/**
 * The hard quantity invariant every sales-order line must satisfy:
 * allocated + fulfilled + cancelled + backordered <= ordered.
 */
export function quantityInvariantHolds(
  ordered: number,
  allocated: number,
  fulfilled: number,
  cancelled: number,
  backordered: number,
): boolean {
  return allocated + fulfilled + cancelled + backordered <= ordered;
}

/**
 * Operator-facing label for a sales-order status. Centralises order-status
 * language (matches StatusBadge casing) so the list filter and any badge never
 * leak a raw snake_case enum to an ordinary operator.
 */
export function soStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case "draft": return "Draft";
    case "confirmed": return "Confirmed";
    case "allocated": return "Allocated";
    case "partially_allocated": return "Partially Allocated";
    case "ready_to_pick": return "Ready to Pick";
    case "partially_fulfilled": return "Partially Fulfilled";
    case "fulfilled": return "Fulfilled";
    case "completed": return "Completed";
    case "returned": return "Returned";
    case "partially_returned": return "Partially Returned";
    case "cancelled": return "Cancelled";
    case "partial": return "Partial";
    default: return humanize(status) || "Unknown";
  }
}

/**
 * Operator-facing label for payment state. Payment is independent of
 * fulfilment status (see sales-order integrity notes); this never folds the
 * two together.
 */
export function paymentStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case "unpaid": return "Unpaid";
    case "pending_payment": return "Pending payment";
    case "paid": return "Paid";
    case "partial": return "Partially paid";
    case "refunded": return "Refunded";
    case "partially_refunded": return "Partially refunded";
    default: return status ? humanize(status) : "Unpaid";
  }
}

/** Financial caption only — never implies allocation, fulfilment, or channel order status. */
export function financialRefundCaption(
  paymentStatus: string | null | undefined,
  _providerSyncStatus?: string | null,
): string {
  return paymentStatusLabel(paymentStatus);
}

/**
 * Three operator-visible layers. Tradivo lifecycle is not the channel order status.
 * Confirmed refunds are described financially; the channel order is not forced to
 * Cancelled or Returned.
 */
export function orderPaymentChannelLayers(opts: {
  orderStatus: string;
  paymentStatus: string | null | undefined;
  channelName: string | null | undefined;
  confirmedChannelRefund: boolean;
  providerCancelStatus?: string | null;
}): { orderLabel: string; paymentLabel: string; channelLabel: string } {
  const channel = providerDisplayName(opts.channelName) || "Channel";
  let channelLabel = channel;
  if (opts.confirmedChannelRefund && opts.paymentStatus === "partially_refunded") {
    channelLabel = `${channel}: Partially Refunded`;
  } else if (opts.confirmedChannelRefund && opts.paymentStatus === "refunded") {
    channelLabel = `${channel}: refund confirmed`;
  } else if (opts.confirmedChannelRefund) {
    channelLabel = `${channel}: refund confirmed`;
  } else if (opts.providerCancelStatus === "confirmed") {
    channelLabel = `${channel}: cancellation confirmed`;
  } else if (opts.orderStatus === "cancelled" && opts.providerCancelStatus === "pending") {
    channelLabel = `${channel}: cancellation pending`;
  } else if (opts.orderStatus === "cancelled" && opts.providerCancelStatus === "error") {
    channelLabel = `${channel}: cancellation not confirmed`;
  } else if (opts.orderStatus === "cancelled" && opts.providerCancelStatus !== "not_applicable") {
    channelLabel = `${channel}: not cancelled`;
  }
  return {
    orderLabel: humanize(opts.orderStatus),
    paymentLabel: paymentStatusLabel(opts.paymentStatus),
    channelLabel,
  };
}

/** Split local vs provider fulfilment; do not silently reconcile inbound buyer cancel. */
export function marketplaceFulfilmentSplit(opts: {
  localOrderStatus: string;
  qtyFulfilled: number;
  providerFulfillmentStatus?: string | null;
  inboundRequestState?: string | null;
  inboundInitiator?: string | null;
  inboundOrderCancelState?: string | null;
}): { tradivoFulfilment: string; ebayFulfilment: string; buyerCancellation: string | null } {
  const tradivo = opts.qtyFulfilled > 0 || opts.localOrderStatus === "fulfilled"
    ? "FULFILLED"
    : (opts.localOrderStatus || "unknown").toUpperCase();
  const ebay = (opts.providerFulfillmentStatus ?? "").trim() || "unknown";
  const actionable = ebayInboundCancelIsActionableBuyer({
    initiator: opts.inboundInitiator,
    requestState: opts.inboundRequestState,
    orderCancelState: opts.inboundOrderCancelState,
  });
  return {
    tradivoFulfilment: tradivo,
    ebayFulfilment: ebay,
    buyerCancellation: actionable ? "REQUESTED / actionable" : null,
  };
}

export function operationalQtyCaption(opts: {
  ordered: number;
  refundedQty: number;
  cancelled: number;
  remainingToFulfil: number;
}): string {
  return `${opts.ordered} ordered · ${opts.refundedQty} refunded · ${opts.cancelled} cancelled · ${opts.remainingToFulfil} remaining to fulfil`;
}

/** Semantic tone for payment state (drives non-colour-only badge styling). */
export type PaymentTone = "unpaid" | "pending" | "paid" | "refunded";

export function paymentStatusTone(status: string | null | undefined): PaymentTone {
  switch (status) {
    case "paid": return "paid";
    case "pending_payment":
    case "partial": return "pending";
    case "refunded":
    case "partially_refunded": return "refunded";
    default: return "unpaid";
  }
}